// Shared event form fields used by both the create page and the manage (edit)
// page. Returns the element plus helpers to read values and surface per-field
// server errors. Mirrors the server validation rules for a fast local check.

import { CATEGORIES } from '../lib/categories';
import type { EventCategory, LocalEventInput } from '../types/event';

export interface EventFormInitial {
  name?: string;
  category?: EventCategory;
  age21Plus?: boolean;
  description?: string;
  address?: string;
  locationName?: string;
  start?: string; // ISO
  end?: string; // ISO
}

export interface EventFormHandle {
  element: HTMLFormElement;
  read: () => { value?: LocalEventInput; errors: Record<string, string> };
  showErrors: (errors: Record<string, string>) => void;
  setBusy: (busy: boolean) => void;
}

const MAX_DURATION_MS = 72 * 60 * 60 * 1000;

// Convert an ISO timestamp to the value a datetime-local input expects (local).
function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fieldError(form: HTMLFormElement, name: string, message: string): void {
  const el = form.querySelector<HTMLElement>(`[data-error="${name}"]`);
  if (el) el.textContent = message;
}

export function renderEventForm(
  initial: EventFormInitial,
  opts: { submitLabel: string }
): EventFormHandle {
  const form = document.createElement('form');
  form.className = 'event-form';
  form.noValidate = true;

  form.innerHTML = `
    <label class="field">
      <span class="field-label">Event name *</span>
      <input name="name" type="text" maxlength="200" required />
      <span class="field-err" data-error="name"></span>
    </label>

    <div class="form-row">
      <label class="field">
        <span class="field-label">Category *</span>
        <select name="category" required>
          ${CATEGORIES.map((c) => `<option value="${c.id}">${c.emoji} ${c.label}</option>`).join('')}
        </select>
        <span class="field-err" data-error="category"></span>
      </label>
      <label class="field checkbox-field">
        <input name="age21Plus" type="checkbox" />
        <span>This event is 21+</span>
        <span class="field-err" data-error="age21Plus"></span>
      </label>
    </div>

    <label class="field">
      <span class="field-label">Venue name</span>
      <input name="locationName" type="text" maxlength="200" placeholder="optional, e.g. Riverfront Park" />
      <span class="field-err" data-error="locationName"></span>
    </label>

    <label class="field">
      <span class="field-label">Address *</span>
      <input name="address" type="text" maxlength="300" required
             placeholder="123 Main St, City, ST 12345" />
      <span class="field-hint">We geocode this to place the event on the map.</span>
      <span class="field-err" data-error="address"></span>
    </label>

    <div class="form-row">
      <label class="field">
        <span class="field-label">Starts *</span>
        <input name="start" type="datetime-local" required />
        <span class="field-err" data-error="start"></span>
      </label>
      <label class="field">
        <span class="field-label">Ends *</span>
        <input name="end" type="datetime-local" required />
        <span class="field-hint">At most 3 days after the start.</span>
        <span class="field-err" data-error="end"></span>
      </label>
    </div>

    <label class="field">
      <span class="field-label">Description</span>
      <textarea name="description" rows="4" maxlength="5000"
                placeholder="optional details"></textarea>
      <span class="field-err" data-error="description"></span>
    </label>

    <button type="submit" class="btn primary submit-btn">${opts.submitLabel}</button>
  `;

  // Prefill.
  const get = <T extends HTMLElement>(sel: string) => form.querySelector<T>(sel)!;
  get<HTMLInputElement>('[name=name]').value = initial.name ?? '';
  get<HTMLSelectElement>('[name=category]').value = initial.category ?? 'community';
  get<HTMLInputElement>('[name=age21Plus]').checked = Boolean(initial.age21Plus);
  get<HTMLInputElement>('[name=locationName]').value = initial.locationName ?? '';
  get<HTMLInputElement>('[name=address]').value = initial.address ?? '';
  get<HTMLInputElement>('[name=start]').value = toLocalInput(initial.start);
  get<HTMLInputElement>('[name=end]').value = toLocalInput(initial.end);
  get<HTMLTextAreaElement>('[name=description]').value = initial.description ?? '';

  function clearErrors(): void {
    form.querySelectorAll<HTMLElement>('.field-err').forEach((e) => (e.textContent = ''));
  }

  function read(): { value?: LocalEventInput; errors: Record<string, string> } {
    clearErrors();
    const errors: Record<string, string> = {};
    const name = get<HTMLInputElement>('[name=name]').value.trim();
    const category = get<HTMLSelectElement>('[name=category]').value as EventCategory;
    const age21Plus = get<HTMLInputElement>('[name=age21Plus]').checked;
    const address = get<HTMLInputElement>('[name=address]').value.trim();
    const locationName = get<HTMLInputElement>('[name=locationName]').value.trim();
    const description = get<HTMLTextAreaElement>('[name=description]').value.trim();
    const startVal = get<HTMLInputElement>('[name=start]').value;
    const endVal = get<HTMLInputElement>('[name=end]').value;

    if (!name) errors.name = 'Name is required.';
    if (!category) errors.category = 'Pick a category.';
    if (!address) errors.address = 'Address is required.';

    const startMs = startVal ? new Date(startVal).getTime() : NaN;
    const endMs = endVal ? new Date(endVal).getTime() : NaN;
    if (!startVal || Number.isNaN(startMs)) errors.start = 'Start time is required.';
    if (!endVal || Number.isNaN(endMs)) errors.end = 'End time is required.';
    if (!errors.start && !errors.end) {
      if (endMs <= startMs) errors.end = 'End must be after start.';
      else if (endMs - startMs > MAX_DURATION_MS) errors.end = 'At most 3 days (72 hours).';
      else if (endMs < Date.now()) errors.end = 'This event has already ended.';
    }

    if (Object.keys(errors).length > 0) return { errors };

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      errors,
      value: {
        name,
        category,
        age21Plus,
        description: description || undefined,
        address,
        locationName: locationName || undefined,
        start: new Date(startMs).toISOString(),
        end: new Date(endMs).toISOString(),
        timezone: tz,
      },
    };
  }

  function showErrors(errors: Record<string, string>): void {
    clearErrors();
    for (const [k, v] of Object.entries(errors)) fieldError(form, k, v);
  }

  function setBusy(busy: boolean): void {
    const btn = get<HTMLButtonElement>('.submit-btn');
    btn.disabled = busy;
    btn.textContent = busy ? 'Working...' : opts.submitLabel;
  }

  return { element: form, read, showErrors, setBusy };
}
